import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";

import { DeliveryCostInput } from "@/components/DeliveryCostInput";
import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

import { API_URL_MARKET_DEMAND_DETAIL } from "../../../../actions";
import client from "../../../../client";
import { formatNumber, getValidationErrorMessage } from "../../../../utils";
import { deliveryCostAmount, hasDeliveryCost } from "../../../../utils/delivery-cost";
import { Field } from "../../../DirectOfferDetails/OfferForm";
import { formatOfferDateTime } from "../../utils";
import { DemandCostBadge, offerCurrencySymbol } from "../OfferCard";
import {
  DeliveryCostProposal,
  DeliveryCostProposalStatus,
  MarketDemandDeliveryCost,
} from "../OfferCard/interfaces";
import { createDeliveryCostProposalSchema, type DeliveryCostProposalValues } from "./schema";

interface DeliveryCostNegotiationModalProps {
  demandId: number;
  // The cost was accepted or countered: the parent closes the modal and refreshes the offers.
  onChanged: () => void;
  onClose: () => void;
}

const DEFAULT_VALUES: DeliveryCostProposalValues = { delivery_cost: "", text: "" };

const STATUS_BADGES: Record<
  DeliveryCostProposalStatus,
  { key: string; variant: "warning" | "success" | "secondary" | "outline" }
> = {
  PENDING: { key: "pending", variant: "warning" },
  ACCEPTED: { key: "accepted", variant: "success" },
  COUNTERED: { key: "countered", variant: "secondary" },
  WITHDRAWN: { key: "withdrawn", variant: "outline" },
};

/** The API's 400 body ({delivery_cost: [...]}, {proposal: [...]}) as one line, else the fallback. */
export const responseError = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  return (data && typeof data === "object" && getValidationErrorMessage(data)) || fallback;
};

const RoundRow = ({
  label,
  byline,
  amount,
  text,
  status,
}: {
  label: string;
  byline: string;
  amount: string;
  text?: string;
  status?: DeliveryCostProposalStatus;
}) => {
  const { t } = useTranslation();
  const badge = status ? STATUS_BADGES[status] : undefined;

  return (
    <li className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="font-medium text-foreground text-sm">{label}</span>
        <span className="text-muted-foreground text-xs">{byline}</span>
        {text && <p className="whitespace-pre-wrap text-foreground text-sm">{text}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="font-semibold text-foreground text-sm">{amount}</span>
        {badge && (
          <Badge variant={badge.variant}>{t(`deliveryCostNegotiation.status.${badge.key}`)}</Badge>
        )}
      </div>
    </li>
  );
};

/**
 * One delivery cost covers a whole multi-commodity demand. The aggregator proposes amounts from the
 * app; here the buyer accepts the aggregator's pending one or counters it, until the cost locks.
 */
export const DeliveryCostNegotiationModal = ({
  demandId,
  onChanged,
  onClose,
}: DeliveryCostNegotiationModalProps) => {
  const { t } = useTranslation();
  const currency = offerCurrencySymbol(t);
  const demandUrl = API_URL_MARKET_DEMAND_DETAIL(demandId);

  const [demand, setDemand] = useState<MarketDemandDeliveryCost | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionError, setActionError] = useState("");
  const [isAccepting, setIsAccepting] = useState(false);

  const resolver = useMemo(
    () =>
      zodResolver(
        createDeliveryCostProposalSchema(t)
      ) as unknown as Resolver<DeliveryCostProposalValues>,
    [t]
  );
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<DeliveryCostProposalValues>({ resolver, defaultValues: DEFAULT_VALUES });

  // Fetch once per demand: re-fetching on re-render would overwrite the demand an
  // accept / counter just returned with a stale read.
  useEffect(() => {
    let active = true;
    client
      .get<MarketDemandDeliveryCost>(demandUrl)
      .then(({ data }) => active && setDemand(data))
      .catch(() => active && setLoadFailed(true));
    return () => {
      active = false;
    };
  }, [demandUrl]);

  const amountLabel = (value: string | number | null | undefined) =>
    `${currency}${formatNumber(deliveryCostAmount(value))}`;

  const onAccept = async (proposal: DeliveryCostProposal) => {
    setActionError("");
    setIsAccepting(true);
    try {
      await client.post(`${demandUrl}accept_delivery_cost/`, { proposal: proposal.id });
      // Done: the parent closes the modal and refreshes the offers.
      onChanged();
    } catch (error) {
      setActionError(responseError(error, t("deliveryCostNegotiation.actionError")));
    } finally {
      setIsAccepting(false);
    }
  };

  const onPropose = handleSubmit(async values => {
    setActionError("");
    try {
      await client.post(`${demandUrl}propose_delivery_cost/`, {
        delivery_cost: Number(values.delivery_cost),
        text: values.text || undefined,
      });
      onChanged();
    } catch (error) {
      setActionError(responseError(error, t("deliveryCostNegotiation.actionError")));
    }
  });

  const pending = demand?.pending_delivery_cost_proposal;
  // The buyer only answers the aggregator: accept or counter its pending proposal.
  const canAnswer = !!demand && !demand.delivery_cost_locked && pending?.side === "AGGREGATOR";
  const isBusy = isAccepting || isSubmitting;

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {/* Same reference as the demand card's title. */}
            {t("deliveryCostNegotiation.title", { reference: demandId })}
          </DialogTitle>
          <DialogDescription>{t("deliveryCostNegotiation.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onPropose} className="flex flex-col gap-4">
          {loadFailed ? (
            <p role="alert" className="text-destructive text-sm">
              {t("deliveryCostNegotiation.loadError")}
            </p>
          ) : !demand ? (
            <div className="flex justify-center p-6">
              <Spinner className="size-6" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3">
                <span className="flex flex-wrap items-center gap-2 text-foreground text-sm">
                  {t("deliveryCostNegotiation.currentCost")}
                  <DemandCostBadge terms={demand} offers={demand.child_offers ?? []} />
                </span>
                <span className="font-semibold text-foreground text-xl">
                  {hasDeliveryCost(demand.delivery_cost)
                    ? amountLabel(demand.delivery_cost)
                    : t("termsComparison.notSpecified")}
                </span>
              </div>

              <div className="flex flex-col gap-2">
                <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                  {t("deliveryCostNegotiation.history")}
                </span>
                <ol className="flex flex-col divide-y divide-border rounded-lg border border-border">
                  <RoundRow
                    label={t("deliveryCostNegotiation.roundOriginal")}
                    byline={formatOfferDateTime(demand.created_datetime)}
                    amount={
                      hasDeliveryCost(demand.original_delivery_cost)
                        ? amountLabel(demand.original_delivery_cost)
                        : t("termsComparison.notSpecified")
                    }
                  />
                  {(demand.delivery_cost_proposals ?? []).map(proposal => (
                    <RoundRow
                      key={proposal.id}
                      label={t(
                        proposal.side === "AGGREGATOR"
                          ? "deliveryCostNegotiation.roundAggregator"
                          : "deliveryCostNegotiation.roundBuyer"
                      )}
                      byline={[
                        proposal.proposed_by_name,
                        formatOfferDateTime(proposal.created_datetime),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                      amount={amountLabel(proposal.delivery_cost)}
                      text={proposal.text}
                      status={proposal.status}
                    />
                  ))}
                </ol>
              </div>

              {demand.delivery_cost_locked ? (
                <p className="text-muted-foreground text-sm">
                  {t("deliveryCostNegotiation.locked")}
                </p>
              ) : !canAnswer ? (
                <p className="text-muted-foreground text-sm">
                  {pending?.side === "BUYER"
                    ? t("deliveryCostNegotiation.awaitingAggregator", {
                        amount: amountLabel(pending.delivery_cost),
                      })
                    : t("deliveryCostNegotiation.noProposal")}
                </p>
              ) : (
                <>
                  <div className="flex flex-col gap-4">
                    <span className="font-semibold text-foreground">
                      {t("deliveryCostNegotiation.proposeTitle")}
                    </span>
                    <Controller
                      control={control}
                      name="delivery_cost"
                      render={({ field, fieldState }) => (
                        <div className="w-full sm:w-56">
                          <Field
                            id="delivery_cost_proposal"
                            label={`${t("deliveryCostNegotiation.amount")} *`}
                            errorMessage={fieldState.error?.message}
                          >
                            <DeliveryCostInput
                              {...field}
                              id="delivery_cost_proposal"
                              currencySymbol={currency}
                              value={field.value ?? ""}
                              invalid={!!fieldState.error}
                            />
                          </Field>
                        </div>
                      )}
                    />
                    <Controller
                      control={control}
                      name="text"
                      render={({ field }) => (
                        <Field id="delivery_cost_text" label={t("deliveryCostNegotiation.message")}>
                          <Textarea {...field} id="delivery_cost_text" value={field.value ?? ""} />
                        </Field>
                      )}
                    />
                  </div>
                </>
              )}

              {actionError && (
                <p role="alert" className="text-destructive text-sm">
                  {actionError}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <WFPButton type="button" variant="secondary" onClick={onClose}>
              {t("common.close")}
            </WFPButton>
            {canAnswer && pending && (
              <>
                <WFPButton type="button" disabled={isBusy} onClick={() => onAccept(pending)}>
                  {isAccepting && <Spinner className="size-4" />}
                  {t("deliveryCostNegotiation.accept", {
                    amount: amountLabel(pending.delivery_cost),
                  })}
                </WFPButton>
                <WFPButton type="submit" variant="secondary" disabled={isBusy}>
                  {isSubmitting && <Spinner className="size-4" />}
                  {t("deliveryCostNegotiation.send")}
                </WFPButton>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
